import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { intersection } from "lodash";
import JobListCard from "./JobListCard";
import JobListFilterBar from "./JobListFilterBar";
import BasePage from "../BasePage";
import Application from "../../apiResources/Application";
import CurrentUser from "../../apiResources/CurrentUser";
import Employer from "../../apiResources/Employer";
import EmployerUser from "../../apiResources/EmployerUser";
import Engagement from "../../apiResources/Engagement";
import Feedback from "../../apiResources/Feedback";
import Placement from "../../apiResources/Placement";
import Job, {
  JobType,
  JobStatus,
  JobPublishedStatus,
} from "../../apiResources/Job";
import { usePagination, useFlash, useDidMountEffect } from "../../hooks";
import Pagination from "../library/Pagination";
import Loader from "../library/Loader";
import Container from "../library/Container";
import FlashMessage from "../shared/general/FlashMessage";
import cache from "../../cache";

const liveStates = [
  "new",
  "interviewing",
  "offer_made",
  "filled",
  "closed",
] as const;
type LiveStatus = (typeof liveStates)[number];

const draftStates = ["draft"] as const;
type DraftStatus = (typeof draftStates)[number];

export type AllStatus = LiveStatus | DraftStatus;

export type FilterData = {
  status: AllStatus[];
  type: JobType[];
  companies: string[];
  users: string[];
};

const defaultFilters: FilterData = {
  status: ["new", "interviewing", "offer_made", "draft"],
  type: [JobType.PERMANENT, JobType.CONTRACT],
  companies: [],
  users: [],
};

const JobsListPage = () => {
  const initial = getInitialData();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(initial.query);
  const [filters, setFilters] = useState<FilterData>(initial.filters);
  const [jobs, setJobs] = useState<Job[]>(initial.jobs);
  const [totalJobs, setTotalJobs] = useState(initial.totalJobs);
  const [user, setUser] = useState<CurrentUser>(initial.user);
  const [companies, setCompanies] = useState<Employer[]>(initial.companies);
  const [users, setUsers] = useState<EmployerUser[]>(initial.users);
  const pagination = usePagination({ page: initial.page });

  const deleteFlash = useFlash();
  const closeFlash = useFlash();

  const loadJobs = useCallback(async () => {
    let didCancel = false;

    setLoading(true);

    const fetchJobs = async () => {
      const response = await Job.where({
        job_type: filters.type,
        status: getJobStatus(filters),
        employer_id: filters.companies,
        by_employer_user_or_notification_recipient_id: filters.users,
        published_status: getPublishedStatus(filters),
        not_deleted: true,
        title: { match: query },
      })
        .includes([
          "currency",
          "applications",
          "engagements",
          "feedbacks",
          "placements",
        ])
        .merge({
          applications: Application.where({ feedback_overdue: true }),
          feedbacks: Feedback.where({ status: "pending" }),
          engagements: Engagement.where({ exclude_pending_partner: true }),
        })
        .order({ created_at: "desc" })
        .page(pagination.page)
        .per(pagination.per)
        .stats({ total: "count" })
        .all();

      if (!didCancel) {
        setJobs(response.data);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setTotalJobs(response.meta.stats.total.count);
        setLoading(false);

        cache.set("job-list", {
          query,
          filters,
          page: pagination.page,
          jobs: response.data,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          totalJobs: response.meta.stats.total.count as number,
          user,
          companies,
          users,
        });
      }
    };

    await fetchJobs();

    return () => {
      didCancel = true;
    };
  }, [filters, pagination.page, query]);

  useDidMountEffect(() => {
    void loadJobs();

    const searchParams = new URLSearchParams(window.location.search);
    const jobWasClosed = Boolean(searchParams.get("job_closed_id"));

    if (jobWasClosed) {
      closeFlash.show();
      searchParams.delete("job_closed_id");

      const newUrl = window.location.href.split("?")[0];
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [loadJobs]);

  useEffect(() => {
    const initialLoad = async () => {
      if (!initial.cached) {
        setLoading(true);
        const userResponse = await CurrentUser.first();

        const employer = (
          await Employer.includes(["children"]).find(
            userResponse.data.companyId
          )
        ).data;

        const employerUsers = (await EmployerUser.all()).data;

        setUser(userResponse.data);
        setCompanies(employer.children);
        setUsers(employerUsers);

        if (
          !userResponse.data.isRootParentEmployer &&
          employerUsers.length > 1
        ) {
          setFilters({
            ...defaultFilters,
            users: [userResponse.data.id],
          });
        } else {
          // This is a bit of a hack to trigger the initial load jobs request.
          // If we call it directly then it can run before the state has been
          // set, which causes issues with the case. By setting to the same value
          // but on a new object we can make the effect run.
          setFilters({ ...defaultFilters });
        }
      }
    };

    void initialLoad();
  }, []);

  const isLoading = loading || !user;

  return (
    <BasePage title="Your jobs">
      <FlashMessage
        message="Job deleted"
        success
        showFlashMessage={deleteFlash.visible}
      />

      <FlashMessage
        message="Job closed"
        success
        showFlashMessage={closeFlash.visible}
      />

      <Container>
        <JobListFilterBar
          query={query}
          filters={filters}
          onQueryChange={(data) => {
            setQuery(data);
            pagination.setPage(1);
          }}
          onFiltersChange={(data) => {
            setFilters(data);
            pagination.setPage(1);
          }}
          companies={companies}
          users={users}
        />

        {isLoading && <Loader text="Loading jobs..." />}

        {!isLoading && jobs.length === 0 && (
          <NoResults>Oops, there are no jobs matching your search!</NoResults>
        )}

        {!isLoading && jobs.length !== 0 && (
          <div data-test-id="jobs-list">
            {jobs.map((job) => (
              <JobListCard
                key={job.id}
                job={job}
                user={user}
                onDelete={() => {
                  cache.delete("job-list");
                  deleteFlash.show();
                  void loadJobs();
                }}
              />
            ))}
            <Pagination pagination={pagination} total={totalJobs} />
          </div>
        )}
      </Container>
    </BasePage>
  );
};

function getInitialData() {
  const cached = cache.get("job-list");

  const jobs = cached?.jobs ?? [];

  return {
    cached: Boolean(cached),
    filters: cached?.filters ?? defaultFilters,
    query: cached?.query ?? "",
    page: cached?.page ?? null,
    jobs: jobs.map(
      (rawJob) =>
        new Job({
          ...rawJob,
          applications: rawJob.applications.map(
            (application) => new Application(application)
          ),
          engagements: rawJob.engagements.map(
            (engagement) => new Engagement(engagement)
          ),
          feedbacks: rawJob.feedbacks.map((feedback) => new Feedback(feedback)),
          placements: rawJob.placements.map(
            (placement) => new Placement(placement)
          ),
        })
    ),
    totalJobs: cached?.totalJobs ?? 0,
    user: cached?.user ?? null,
    companies: cached?.companies ?? [],
    users: cached?.users ?? [],
  };
}

const NoResults = styled.h2`
  display: flex;
  margin: 2rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

function getPublishedStatus(filters: FilterData) {
  const result: JobPublishedStatus[] = [];

  const hasDraft = intersection(filters.status, draftStates).length !== 0;
  if (hasDraft) {
    result.push(JobPublishedStatus.DRAFT, JobPublishedStatus.REVIEW);
  }

  const hasLive = intersection(filters.status, liveStates).length !== 0;
  if (hasLive) {
    result.push(JobPublishedStatus.LIVE);
  }

  return result;
}

function getJobStatus(filters: FilterData) {
  const result: JobStatus[] = [];

  if (filters.status.includes("new") || filters.status.includes("draft")) {
    result.push(JobStatus.NEW, JobStatus.OPEN);
  }

  if (filters.status.includes("interviewing")) {
    result.push(JobStatus.INTERVIEW);
  }

  if (filters.status.includes("offer_made")) {
    result.push(JobStatus.OFFER);
  }

  if (filters.status.includes("filled")) {
    result.push(JobStatus.FILLED);
  }

  if (filters.status.includes("closed")) {
    result.push(JobStatus.EXPIRED, JobStatus.CLOSED);
  }

  return result;
}

export default JobsListPage;
